import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import LandscapeIcon from '@mui/icons-material/Landscape';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import WhatToSeeListRow from 'components/WhatToSeeListRow';
import WhatToSeeListAdd from 'components/WhatToSeeListAdd';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  WhatToSeeListRefetchQuery,
} from '__generated__/WhatToSeeListRefetchQuery.graphql';
import { WhatToSeeList_data$key } from '__generated__/WhatToSeeList_data.graphql';

type Props = {
  data: WhatToSeeList_data$key;
};

const WhatToSeeList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ whatToSees }, { refetch, loading }] = useAvoidSuspense<
    WhatToSeeListRefetchQuery,
    WhatToSeeList_data$key
  >(
    graphql`
      fragment WhatToSeeList_data on Query
      @refetchable(queryName: "WhatToSeeListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        whatToSees(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...WhatToSeeListRow_whatToSee
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = whatToSees?.__id;

  const [deleteWhatToSee, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = whatToSees ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<LandscapeIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableHeadCell center name="destination">
              Vieta
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <WhatToSeeListRow
            key={node.id}
            whatToSee={node}
            onDelete={deleteWhatToSee}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <WhatToSeeListAdd connectionID={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default WhatToSeeList;
